<script lang="ts" setup>
import HeadlineListSection from '../../HeadlineListSection.vue';
import ParagrahNote from '../../ParagrahNote.vue';
import WhereToConsultCard from '../../WhereToConsult/WhereToConsultCard.vue';
import ShortenedRepaymentPeriodImg from '/home/runner/_work/shadow/shadow/packages/app/node_modules/.cache/home/runner/_work/shadow/shadow/packages/app/src/components/templates/ProductLpTemplate/mortgage/WhereToConsultCard/07_shortened_repayment_period.png/07_shortened_repayment_period.webp';
import ReducedRepaymentImg from '/home/runner/_work/shadow/shadow/packages/app/node_modules/.cache/home/runner/_work/shadow/shadow/packages/app/src/components/templates/ProductLpTemplate/mortgage/WhereToConsultCard/08_reduced_repayment.png/08_reduced_repayment.webp';
import MortgageRefinancingImg from '/home/runner/_work/shadow/shadow/packages/app/node_modules/.cache/home/runner/_work/shadow/shadow/packages/app/src/components/templates/ProductLpTemplate/mortgage/WhereToConsultCard/09_mortgage_refinancing.png/09_mortgage_refinancing.webp';
import PinchImageModal from '~theme/components/organisms/PinchImageModal.vue';

defineProps<{
  imageNumber: number;
}>();
</script>

<template>
  <WhereToConsultCard
    title="住宅ローンのおすすめの選び方"
    :image-number="imageNumber"
  >
    <p>
      住宅ローンの負担を軽減するために、以下の5つのポイントを確認しておきましょう。
    </p>
    <HeadlineListSection>
      <li><a href="#card4Headline1">トータルコストを考える</a></li>
      <li><a href="#card4Headline2">住宅ローン控除を最大限活用する</a></li>
      <li><a href="#card4Headline3">自治体独自の制度の有無を確認する</a></li>
      <li>
        <a href="#card4Headline4">まとまった資金ができたら繰り上げ返済をする</a>
      </li>
      <li>
        <a href="#card4Headline5"
          >今より金利が低い住宅ローンがあれば借り換えをする</a
        >
      </li>
    </HeadlineListSection>

    <h3 id="card4Headline1">トータルコストを考える</h3>
    <p>住宅ローンの借入時や繰り上げ返済時には、手数料などがかかります。</p>
    <p>
      特に借入時は、金融機関に支払う事務手数料や司法書士に支払う報酬などの他にも、引っ越し費用や家具代などさまざまな費用がかかります。
    </p>
    <p>
      資金が不足しないよう、金利だけでなく手数料や諸費用を踏まえたトータルコストを考え、住宅ローンの借入額を決定しましょう。
    </p>

    <h3 id="card4Headline2">住宅ローン控除を最大限活用する</h3>
    <p>
      住宅ローンを組んで住宅を購入した場合、一定の要件を満たせば住宅ローン控除を利用することにより税金の控除を受けられます。
    </p>
    <p>
      住宅ローン控除額の計算方法：年末のローン残高×0.7％（控除期間は13年間※中古住宅は10年間）<br />
      2024年8月時点の情報
    </p>
    <ParagrahNote>
      参照：<NuxtLink
        href="https://www.nta.go.jp/taxes/shiraberu/taxanswer/shotoku/1213.htm"
        external
        target="_blank"
        >国税庁_認定住宅の新築等をした場合(住宅借入金等特別控除)</NuxtLink
      >
    </ParagrahNote>
    <p>
      控除額の上限は住宅の条件により異なり、例えば新築の省エネ基準を満たした住宅の場合は最大で21万円/年の控除を受けられます。
    </p>
    <p>
      住宅ローン控除を受けるには、控除を受ける最初の年のみ確定申告が必要となるので、忘れずに申告を行いましょう。なお、2年目以降は年末調整により控除を受けることが可能です。（会社員の場合）
    </p>

    <h3 id="card4Headline3">自治体独自の制度の有無を確認する</h3>
    <p>
      自治体によっては、住宅購入費用に充てられる補助金・助成金制度を設けているところがあります。住宅ローンを利用する前に、<NuxtLink
        href="https://www.j-reform.com/reform-support/"
        external
        target="_blank"
        >住宅リフォーム推進協議会</NuxtLink
      >の検索サイトで自治体独自の制度がないかを確認しましょう。
    </p>
    <p>
      また住宅ローンに関しても、自治体と住宅金融支援機構の連携により、フラット35の借入金利を一定期間引き下げる制度が利用できる場合があります。該当する自治体は<NuxtLink
        href="https://www.flat35.com/loan/flat35kosodate/index.html"
        external
        target="_blank"
        >住宅金融支援機構</NuxtLink
      >のサイトで検索できるので、制度の詳細とあわせて調べてみるのがおすすめです。
    </p>

    <h3 id="card4Headline4">まとまった資金ができたら繰り上げ返済をする</h3>
    <p>
      まとまった資金ができたときは、住宅ローンの繰り上げ返済を検討しましょう。
    </p>
    <p>
      繰り上げ返済とは、毎月の返済分とあわせて、住宅ローン残高の一部や全部を前倒しで返済することをいいます。繰り上げ返済を行うと、繰り上げ分にかかるはずだった利息の支払いがなくなるため、当初の予定よりも返済総額を減らすことが可能です。
    </p>
    <p>
      繰り上げ返済は、返済期間を短縮する「返済期間短縮型」と毎月の返済額を減らす「返済額軽減型」があります。
    </p>
    <PinchImageModal :src="ShortenedRepaymentPeriodImg" />
    <PinchImageModal :src="ReducedRepaymentImg" />
    <p>
      より利息軽減効果が高いのは、返済期間短縮型の繰り上げ返済です。毎月の返済が負担でなければ、期間短縮型の繰り上げ返済を行うとよいでしょう。
    </p>

    <h3 id="card4Headline5">
      今より金利が低い住宅ローンがあれば借り換えをする
    </h3>
    <p>
      住宅ローンの返済期間は長いため、返済中に金利が変動する可能性があります。状況に応じて、より金利の低い住宅ローンへの借り換えや、金利タイプの切り替えを検討するのがおすすめです。
    </p>
    <PinchImageModal :src="MortgageRefinancingImg" />
    <ParagrahNote>
      ※金利は一例です。借り換えには手数料等がかかります。
    </ParagrahNote>
    <p>
      また、変動金利から固定金利に借り換えることで、将来の金利上昇リスクを回避する・抑えることもできるでしょう。
    </p>
    <p>
      ただし、住宅ローンの「借り換え」には手数料がかかるため、金利の低い銀行や手数料（融資手数料+保証料）の安い銀行で借り換えるのがおすすめです。
    </p>
  </WhereToConsultCard>
</template>

<style module lang="scss"></style>
